import React, { useState, useEffect } from "react";
import { Play, Calendar, AlertCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import { isFutureDate } from "../../hooks/commonFunction";

const ResumeSubscriptionModal = ({
  isOpen,
  onClose,
  onConfirm,
  subscriptionName,
  schedules = [],
  isSubmitting = false,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (schedules && schedules.length > 0) {
      // Select the first future scheduled date by default
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const firstFutureScheduled = schedules.find(
        (s) => s.status === "scheduled" && new Date(s.scheduled_date) > today
      );

      if (firstFutureScheduled) {
        setSelectedDate(firstFutureScheduled.scheduled_date);
      }
    }
  }, [schedules]);

  const handleConfirm = () => {
    if (!selectedDate) {
      toast.error("Please select a delivery date");
      return;
    }

    if (!isFutureDate(selectedDate)) {
      toast.error("Please select a future date for delivery");
      return;
    }

    onConfirm(selectedDate);
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg overflow-hidden max-w-md w-full mx-4">
        {/* Gradient Header */}
        <div className="bg-hf-gradient px-6 py-4 text-center">
          <h3 className="text-xl font-bold text-white">Resume Subscription</h3>
        </div>

        <div className="p-6">
          {/* Icon and Message */}
          <div className="flex flex-col items-center text-center mb-6">
            <p className="text-gray-600">
              {subscriptionName && (
                <span className="font-semibold">{subscriptionName}</span>
              )}
              ?
            </p>
          </div>

          {/* Date Selection */}
          {schedules && schedules.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Select Next Delivery Date:
              </label>
              <div className="max-h-72 overflow-y-auto border border-gray-300 rounded-lg">
                {schedules
                  .filter((s) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return (
                      s.status === "scheduled" &&
                      new Date(s.scheduled_date) > today
                    );
                  })
                  .map((schedule) => (
                    <label
                      key={schedule.id}
                      className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                        selectedDate === schedule.scheduled_date
                          ? "bg-red-50 border-l-4 border-l-red-600"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="deliveryDate"
                        value={schedule.scheduled_date}
                        checked={selectedDate === schedule.scheduled_date}
                        onChange={() =>
                          setSelectedDate(schedule.scheduled_date)
                        }
                        className="w-4 h-4 text-red-600 focus:ring-red-500"
                      />
                      <span
                        className={`ml-3 text-sm ${
                          selectedDate === schedule.scheduled_date
                            ? "font-semibold text-red-700"
                            : "text-gray-700"
                        }`}
                      >
                        {new Date(schedule.scheduled_date).toLocaleDateString(
                          "en-US",
                          {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </label>
                  ))}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 border-2 border-gray-400 text-gray-400 rounded-lg font-semibold hover:bg-gray-300 hover:text-black transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSubmitting || !selectedDate}
              className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? "Resuming..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeSubscriptionModal;
